import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { Save, ArrowLeft, Search, Tag, Paperclip, History, Zap, Trash2 } from 'lucide-react';
import UniversalLookup from './UniversalLookup';
import AttachmentsModal from './AttachmentsModal';
import AuditHistoryModal from './AuditHistoryModal';
import StatusHistoryModal from './StatusHistoryModal';
import { usePermissions } from './usePermissions';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';

const TagoutDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const { canDelete } = usePermissions('tagouts');
  const [activeTab, setActiveTab] = useState('Tag');
  const [loading, setLoading] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [count, setCount] = useState(0);

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const [data, setData] = useState({
    tagoutnum: '', description: '', assetnum: '', location: '', required_state: 'OFF',
    siteid: session.site_id || '', orgid: session.org_id || ''
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '' });

  useEffect(() => {
    if (!isNew) axios.get(`/api/tagouts/${id}`).then(res => setData(res.data));
  }, [id, isNew]);

  const fetchCount = useCallback(async () => {
    if (!data.tagoutnum || isNew) return;
    try {
      const res = await axios.get(`/api/attachments/count/TAGOUT/${data.tagoutnum}`);
      setCount(res.data.count || 0);
    } catch (err) { console.error(err); }
  }, [data.tagoutnum, isNew]);

  useEffect(() => { fetchCount(); }, [fetchCount]);

  useEffect(() => {
    const openLog = () => setShowAudit(true);
    window.addEventListener('GLOBAL_LOG_HANDLER', openLog);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLog);
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      await axios.post('/api/tagouts/save', data);
      toast.success(t('tagouts.details.saveSuccess', 'Tagout Point Saved!'));
      if (isNew) navigate(`/tagouts/${data.tagoutnum}`);
    } catch (err) { toast.error(t('common.save_fail', 'Save failed')); }
    finally { setLoading(false); }
  };

  const handleDelete = () => {
    Swal.fire({
      ...swalTheme(),
      title: t('common.are_you_sure', 'Are you sure?'), text: t('common.cannot_be_undone', 'This action cannot be undone!'),
      icon: 'warning', showCancelButton: true, confirmButtonColor: swalDangerColor(),
      confirmButtonText: t('common.yes_delete', 'Yes, delete it!')
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`/api/tagouts/${data.tagoutnum}`);
          toast.success(t('common.delete_success', 'Deleted successfully'));
          navigate('/tagouts');
        } catch (err) { toast.error(err.response?.data?.detail || t('common.delete_fail', 'Delete failed')); }
      }
    });
  };

  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);

  return (
    <div className="flex flex-col h-screen bg-surface-app text-[11px] font-sans uppercase font-bold tracking-tighter">
      <UniversalLookup {...lookup} onClose={() => setLookup({...lookup, isOpen: false})} onSelect={(r) => setData({...data, [lookup.field]: r.value || r.assetnum || r.location})} />
      {!isNew && <AttachmentsModal isOpen={isDocModalOpen} onClose={() => {setIsDocModalOpen(false); fetchCount();}} ownertable="TAGOUT" ownerid={data.tagoutnum} />}
      {showHistoryModal && <StatusHistoryModal isOpen={showHistoryModal} appName="TAGOUT" recordId={data.tagoutnum} onClose={() => setShowHistoryModal(false)} />}
      <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName="tagouts" recordId={data.tagoutnum} />

      <div className="bg-surface-card p-2 border-b border-line flex justify-between items-center shadow-sm z-20 font-black">
        <div className="flex gap-6 ml-4">
          <button onClick={() => navigate('/tagouts')} className="p-1 hover:bg-surface-panel rounded-full text-primary"><ArrowLeft size={18} /></button>
          <HeaderField label={t('tagouts.fields.tagOutNum', 'Tag Num')} value={data.tagoutnum} isBold width="w-32" disabled={!isNew} onChange={v => setData({...data, tagoutnum: v})} />
          <HeaderField label={t('tagouts.fields.description', 'Description')} value={data.description} onChange={v => setData({...data, description: v})} width="w-[300px]" />
          <HeaderField label={t('common.site', 'Site')} value={data.siteid} disabled width="w-24" />
        </div>
        <div className="flex gap-2 mr-4">
            <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 text-primary rounded-sm hover:bg-primary hover:text-white transition-all">
                <Paperclip size={18} />
                {count > 0 && <span className="absolute -top-1 -right-1 bg-status-danger text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center border border-white">{count}</span>}
            </button>
            <button onClick={() => setShowHistoryModal(true)} className="p-2 text-primary hover:bg-primary-soft rounded"><History size={16}/></button>
            {!isNew && canDelete && (
              <button onClick={handleDelete} className="p-2 text-status-danger hover:bg-status-danger-bg rounded transition-colors" title={t('common.delete', 'Delete')}>
                <Trash2 size={16}/>
              </button>
            )}
            <button onClick={handleSave} disabled={loading} className="bg-primary text-white px-8 py-2 rounded flex items-center gap-2 hover:bg-primary-dark shadow-lg">
               <Save size={14} /> {loading ? t('common.saving', 'SAVING...') : t('common.save', 'SAVE')}
            </button>
        </div>
      </div>

      <div className="flex bg-primary-dark text-white shadow-md">
        <button className="px-10 py-3 flex items-center gap-2 font-black bg-surface-card text-primary-dark"><Zap size={14}/> {t('tagouts.details.tabIsolation', 'Isolation Detail')}</button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 bg-surface-panel">
          <div className="max-w-4xl mx-auto space-y-6">
            <SectionCard title={t('tagouts.details.pointInformation', 'Tagout / Lockout Point Information')}>
              <div className="grid grid-cols-2 gap-8 p-4">
                <EditableField label={t('tagouts.fields.tagNumber', 'Tag Number')} value={data.tagoutnum} disabled={!isNew} />
                <EditableField label={t('tagouts.fields.requiredState', 'Required State')} value={data.required_state} onChange={v => setData({...data, required_state: v})} placeholder={t('tagouts.fields.requiredStatePlaceholder', 'e.g. CLOSED, OFF, LOCKED')} />
                <EditableField label={t('tagouts.fields.assetNumber', 'Asset Number')} value={data.assetnum} onSearch={() => setLookup({isOpen: true, title: t('tagouts.lookup.selectAsset', 'Select Asset'), type: 'assets', field: 'assetnum'})} />
                <EditableField label={t('common.location', 'Location')} value={data.location} onSearch={() => setLookup({isOpen: true, title: t('tagouts.lookup.selectLocation', 'Select Location'), type: 'locations', field: 'location'})} />
                <div className="col-span-2"><EditableField label={t('tagouts.fields.detailedDescription', 'Detailed Description')} value={data.description} onChange={v => setData({...data, description: v})} /></div>
              </div>
            </SectionCard>
          </div>
      </div>
    </div>
  );
};

const HeaderField = ({ label, value, onChange, width, isBold, disabled }) => (
  <div className="flex flex-col border-r border-line px-4 last:border-0">
    <label className="text-[8px] text-ink-secondary font-black uppercase tracking-widest mb-0.5 italic">{label}</label>
    <input className={`${width} border-b border-transparent outline-none py-0.5 font-black uppercase ${isBold ? 'text-primary-dark text-[12px]' : 'text-primary'} bg-transparent focus:border-primary`}
           value={value || ''} disabled={disabled} onChange={e => onChange && onChange(e.target.value)} />
  </div>
);

const SectionCard = ({ title, children }) => (
  <div className="bg-surface-card border border-line rounded shadow-sm overflow-hidden transition-all hover:shadow-md">
    <div className="p-2 bg-surface-panel border-b border-line font-black text-ink-secondary uppercase text-[9px] tracking-widest flex items-center gap-2">
      <div className="w-1.5 h-3 bg-primary rounded-full"></div> {title}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const EditableField = ({ label, value, onChange, onSearch, type = "text", disabled, placeholder }) => (
  <div className="flex flex-col gap-1 border-b border-line group focus-within:border-primary transition-all py-1">
    <label className="text-ink-secondary font-black text-[8px] uppercase tracking-widest italic">{label}</label>
    <div className="flex items-center justify-between pb-0.5">
      <input type={type} disabled={disabled} placeholder={placeholder} className="outline-none font-black text-primary bg-transparent w-full text-[11px] placeholder:font-normal placeholder:normal-case placeholder:opacity-40"
        value={value || ''} onChange={e => onChange && onChange(e.target.value)} />
      {onSearch && <Search size={12} className="text-primary cursor-pointer hover:text-primary-dark" onClick={onSearch} />}
    </div>
  </div>
);

export default TagoutDetails;
